import * as Moment from 'moment';
import 'moment-duration-format';
import * as VitaloJak1Api from '../../../misc/api/DeviceLinkApi/VitaloJak1';
import { SessionScheduleItem, Subject, Query, Visit, CoughSession, DeviceTypes, CoughSessionStatusEnum, SubjectTypes } from '../../../misc/api/apiTypes';
import { FailedToWriteConfigurationMessage, FailedToConfigureMessage, sessionCreatedDeviceNotConfiguredMessage } from '../../../misc/api/DeviceLinkApi/VitaloJak1Messages';
import { DeviceItem } from '../Confirmation';
import { WebPortalClient, StudyScheduleSession } from "webportalclient";
import { useWebPortalApiClient, useWebPortalLegacyClient, WebPortalLegacyClientInternal } from '../../../misc/api/WebPortalTokenClient';

declare const rootUrl: string;

const createVisitWrapperIfNotExists = async (legacyClient: WebPortalLegacyClientInternal, visitScheduleId: string, subjectId: string): Promise<Visit> => {
    const visits: Query<Visit> = await legacyClient.getVisits({
        Filters: [{
            Member: 'SubjectId',
            Values: [subjectId],
        },
            {
                Member: 'VisitScheduleItemId',
                Values: [visitScheduleId],
            }]
        });

        if(visits.Data.length < 1) {
            return legacyClient.createVisit(subjectId, visitScheduleId);
    }

    return visits.Data[0];
}

const createSessionWrapper = async (webPortalClient: WebPortalClient, visitId: string, sessionSchedule: StudyScheduleSession, technician: string, serialNumber: string, mediaSerialNumber: string, subjectId: string) => {
    const getVisitById = await webPortalClient.getVisits(undefined, `id eq ${visitId}`);

    const getSessions = await webPortalClient.getCoughSessions(undefined, `visit/subjectId eq ${subjectId} and name eq '${sessionSchedule.label}' and visit/name eq '${getVisitById.value[0].name}'`, undefined, "visit");

    const checkIfSessionExists = getSessions.value[0];
    let coughSession;

    if (checkIfSessionExists === undefined) {

        coughSession = await webPortalClient.postCoughSession({

            name: sessionSchedule.label,
            visitId: visitId,
            technician: technician,
            serialNumber: serialNumber,
            mediaSerialNumber: mediaSerialNumber,
            status: CoughSessionStatusEnum.AwaitingData,
            deviceType: DeviceTypes.VitaloJAK1
        });
    } else {
        coughSession = await webPortalClient.patchCoughSession({

            name: sessionSchedule.label,
            visitId: visitId,
            technician: technician,
            serialNumber: serialNumber,
            mediaSerialNumber: mediaSerialNumber,
            status: CoughSessionStatusEnum.AwaitingData,
            deviceType: DeviceTypes.VitaloJAK1
        
        }, getSessions.value[0].id);
    }

    return coughSession;
}

export const ConfigureVitaloJak1 = async (webPortalClient: WebPortalClient, legacyClient: WebPortalLegacyClientInternal, userEmail: string, subject: Subject, sessionToConfigure: StudyScheduleSession, selectedDeviceId: string, device: DeviceItem, isNotConfigured: boolean, onError: (message: string) => void, onComplete: () => void) => {
    try {
        const coughSettings = await legacyClient.getStudyCoughSettings();
        const durationSetting = coughSettings.MinimumRecordingDuration;

        const minPercentage = durationSetting / 100;
        const minimumRecordingDuration = Moment.duration(86400000 * minPercentage).format("d.hh:mm:ss", { trim: false });

        let visitWrapperResultId;
        let sessionWrapperResultId;
        if (isNotConfigured) {
            const found = await (await legacyClient.getCoughSessions([{ Member: 'Id', Values: [sessionToConfigure.id] }]))?.Data[0];
            visitWrapperResultId = await legacyClient.getVisitIdByExternalCode(found.Id);
            sessionWrapperResultId = found.Id;
        } else {
            const visitWrapperResult = await createVisitWrapperIfNotExists(legacyClient, sessionToConfigure.studyScheduleVisitId, subject.Id);
            const sessionWrapperResult = await createSessionWrapper(webPortalClient, visitWrapperResult.Id, sessionToConfigure, userEmail, device.SerialNumber, device.MediaSerialNumber, subject.Id);
            visitWrapperResultId = visitWrapperResult.Id;
            sessionWrapperResultId = sessionWrapperResult.id;
            
        }

        let subjectState: VitaloJak1Api.SubjectState;
        switch (subject.SubjectType.Id) {
            case SubjectTypes.InStudy:
                subjectState = VitaloJak1Api.SubjectState.InStudy;
                break;
            case SubjectTypes.Training:
                subjectState = VitaloJak1Api.SubjectState.Practice;
                break;
            case SubjectTypes.Certification:
                subjectState = VitaloJak1Api.SubjectState.Certification;
                break;
        }

        const configuration: VitaloJak1Api.VitaloJak1Configuration = {
            deviceSerialNumber: device.SerialNumber,
            mediaSerialNumber: device.MediaSerialNumber,
            assessmentKey: sessionToConfigure.id,
            assessmentLabel: sessionToConfigure.label,
            assessmentGuid: sessionWrapperResultId,
            subjectGuid: subject.Id,
            subjectNumber: subject.SubjectNumber,
            subjectState: subjectState,
            visitGuid: visitWrapperResultId,
            minimumRecordingDuration: minimumRecordingDuration,
        };
        const result = await VitaloJak1Api.ApiSetConfiguration(selectedDeviceId, configuration);

        if (!result) {
            return onError(FailedToWriteConfigurationMessage);
        }

        onComplete();

        await webPortalClient.patchCoughSession({
            configured: true,
        }, sessionWrapperResultId);

        return;
    }
    catch (err) {
        onError(FailedToConfigureMessage);
    }
}
